import React, { createContext, useContext, useState, useCallback } from 'react';
import Toast, { ToastProps } from '../components/ui/Toast';

interface ToastItem extends Omit<ToastProps, 'onClose'> {
  id: string;
}

interface ToastContextValue {
  showToast: (props: Omit<ToastProps, 'id' | 'onClose'>) => string;
  closeToast: (id: string) => void;
  clearAllToasts: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export interface ToastProviderProps {
  children: React.ReactNode;
}

/**
 * 토스트 컨텍스트 제공자
 *
 * 토스트 알림을 전역에서 쉽게 사용할 수 있도록 컨텍스트를 제공합니다.
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  // 토스트 표시
  const showToast = useCallback((props: Omit<ToastProps, 'id' | 'onClose'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    setToasts((prevToasts) => [
      ...prevToasts,
      {
        ...props,
        id,
      },
    ]);

    return id;
  }, []);

  // 토스트 닫기
  const closeToast = useCallback((id: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  // 모든 토스트 닫기
  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, closeToast, clearAllToasts }}>
      {children}
      {toasts.map((toast) => (
        <Toast key={toast.id} {...toast} onClose={closeToast} />
      ))}
    </ToastContext.Provider>
  );
};

/**
 * Toast 컨텍스트 사용을 위한 훅
 *
 * @example
 * const { showToast } = useToast();
 *
 * // 기본 토스트 표시
 * showToast({
 *   type: 'success',
 *   title: '성공',
 *   message: '작업이 완료되었습니다.'
 * });
 *
 * // 커스텀 설정으로 토스트 표시
 * showToast({
 *   type: 'error',
 *   title: '오류 발생',
 *   message: '요청을 처리할 수 없습니다.',
 *   duration: 5000,
 *   position: 'bottom-center'
 * });
 */
export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  const { showToast, closeToast, clearAllToasts } = context;

  // 타입별 헬퍼 함수
  const success = useCallback(
    (
      message: string,
      options?: Partial<Omit<ToastProps, 'id' | 'onClose' | 'message' | 'type'>>,
    ) => {
      return showToast({
        type: 'success',
        message,
        ...options,
      });
    },
    [showToast],
  );

  const error = useCallback(
    (
      message: string,
      options?: Partial<Omit<ToastProps, 'id' | 'onClose' | 'message' | 'type'>>,
    ) => {
      return showToast({
        type: 'error',
        message,
        ...options,
      });
    },
    [showToast],
  );

  const info = useCallback(
    (
      message: string,
      options?: Partial<Omit<ToastProps, 'id' | 'onClose' | 'message' | 'type'>>,
    ) => {
      return showToast({
        type: 'info',
        message,
        ...options,
      });
    },
    [showToast],
  );

  const warning = useCallback(
    (
      message: string,
      options?: Partial<Omit<ToastProps, 'id' | 'onClose' | 'message' | 'type'>>,
    ) => {
      return showToast({
        type: 'warning',
        message,
        ...options,
      });
    },
    [showToast],
  );

  return {
    showToast,
    closeToast,
    clearAllToasts,
    success,
    error,
    info,
    warning,
  };
};

export default ToastProvider;
